import {ContentHotel} from '@findhotel/sapi/dist/types/packages/core/src/types'
import {difference} from 'ramda'
import {GuestType} from 'types/Hotel'

import {toPercentage} from '@vio/core/src/utils/number'

const RATING_EXCLUDED_FROM_DISPLAY = ['overall', 'pricing']
const RATING_PRIORITY_ORDER = [
  'facilities',
  'cleanliness',
  'rooms',
  'service',
  'location',
  'dining'
]

type GuestTypeKey = keyof GuestType

const getTotalNumberOfGuests = (guestTypes: Partial<GuestType>): number =>
  Object.keys(guestTypes).reduce(
    (acc, key: GuestTypeKey) => acc + guestTypes[key],
    0
  )

export const calculateGuestTypePercentages = (
  guestTypes: GuestType
): Array<{key: GuestTypeKey; value: number}> => {
  const total = getTotalNumberOfGuests(guestTypes)

  return Object.keys(guestTypes).map((key: GuestTypeKey) => {
    return {key, value: toPercentage(guestTypes[key], total) || 0}
  })
}

export const getReviewAvailability = (hotel: ContentHotel) => {
  const hasReviews =
    Boolean(hotel?.reviewCount) && Boolean(hotel?.guestRating?.overall)

  const hasSentiments = (hotel?.sentiments || []).length > 0

  const hasGuestsTypes = getTotalNumberOfGuests(hotel?.guestType || {}) > 0

  const hasSummary =
    Object.keys(hotel?.guestRating || {}).filter(
      key => !RATING_EXCLUDED_FROM_DISPLAY.includes(key)
    ).length > 0

  const hasAnyReviewComponent =
    hasSentiments || hasSummary || hasGuestsTypes || hasReviews

  return {
    hasAnyReviewComponent,
    hasReviews,
    hasSentiments,
    hasGuestsTypes,
    hasSummary
  }
}

/**
 * Filter out excluded keys, order keys by the priority and append
 * any keys not listed in the priority order at the end
 * @param ratings - list of rating keys
 * @returns modified/ordered list of rating keys available for display
 */
export const orderedAvailableRatings = (ratings: string[]) => {
  const filteredInputRatings = ratings.filter(
    rating => !RATING_EXCLUDED_FROM_DISPLAY.includes(rating)
  )

  const orderedAvailable = RATING_PRIORITY_ORDER.filter(ordered =>
    filteredInputRatings.includes(ordered)
  )

  return orderedAvailable.concat(
    difference(orderedAvailable, filteredInputRatings)
  )
}
